import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MapPin, Check, ArrowUpRight, Sparkles } from "lucide-react";
import { findAreaBySlug } from "@/data/localAreas";
import { SERVICES, PRACTICE, ASSETS } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";
import { localizeService } from "@/i18n/data.es";
import SunDivider from "@/components/site/SunDivider";
import ContactForm from "@/components/site/ContactForm";
import Seo from "@/components/site/Seo";
import { breadcrumbSchema, dentalClinicSchema } from "@/lib/seo";

const SITE_URL = "https://primelogichub.com";

export default function LocalAreaPage() {
  const { slug } = useParams();
  const { t, lang } = useT();
  const area = findAreaBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!area) return <Navigate to="/" replace />;

  const featured = area.featuredServices
    .map((s) => SERVICES.find((x) => x.slug === s))
    .filter(Boolean)
    .map((s) => localizeService(s, lang));

  const localBusiness = {
    ...dentalClinicSchema(),
    "@id": `${SITE_URL}/dentist-in/${area.slug}/#dentist`,
    areaServed: {
      "@type": "City",
      name: area.city,
      containedInPlace: { "@type": "State", name: "Virginia" },
    },
    name: `Smiles of Loudoun — Dentist for ${area.city}, ${area.state}`,
    slogan: area.tagline,
  };

  return (
    <article data-testid={`local-page-${area.slug}`}>
      <Seo
        title={`Dentist in ${area.city}, ${area.state}`}
        description={`Serving ${area.city}, ${area.state} families with cosmetic, family and implant dentistry. ${area.distance}. Book with Smiles of Loudoun — call ${PRACTICE.phone}.`}
        path={`/dentist-in/${area.slug}`}
        jsonLd={[
          localBusiness,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: `Dentist in ${area.city}`, path: `/dentist-in/${area.slug}` },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="px-6 md:px-10 lg:px-16 pt-14 md:pt-20 pb-10 md:pb-14 text-center">
        <div className="font-mono-eyebrow text-[var(--text-muted)]">
          {t("Serving")} · {area.city}, {area.state}
        </div>
        <h1 className="font-serif-display text-[var(--primary)] mt-4 text-[44px] md:text-[72px] leading-[1.02] tracking-tight max-w-4xl mx-auto">
          {t("A dentist your")}{" "}
          <span className="italic text-[var(--accent)]">
            {area.city}
          </span>{" "}
          {t("family will trust.")}
        </h1>
        <SunDivider className="mt-6" />
        <p className="mt-5 max-w-2xl mx-auto text-[var(--text-muted)] text-[17px] leading-relaxed">
          {area.tagline}
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            data-testid="local-book-btn"
            href={PRACTICE.appointmentUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]"
          >
            {t("Request Appointment")} <ArrowUpRight className="w-4 h-4" />
          </a>
          <a
            data-testid="local-call-btn"
            href={PRACTICE.phoneHref}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)]"
          >
            <Phone className="w-4 h-4" /> {PRACTICE.phone}
          </a>
        </div>
      </section>

      {/* Intro + highlights */}
      <section className="px-6 md:px-10 lg:px-16 py-14 md:py-20 border-y border-[var(--border)] bg-[var(--surface)]/40">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 max-w-6xl mx-auto">
          <div className="lg:col-span-7">
            <div className="font-mono-eyebrow text-[var(--accent)]">
              {t("Neighbors we love")}
            </div>
            <h2 className="font-serif-display text-[var(--primary)] mt-3 text-[36px] md:text-[52px] leading-[1.05] tracking-tight">
              {t("Care built around")}{" "}
              <span className="italic text-[var(--accent)]">
                {area.city}
              </span>{" "}
              {t("families.")}
            </h2>
            <p className="mt-6 text-[17px] leading-relaxed text-[var(--text-main)]/85">
              {area.intro}
            </p>
            <ul className="mt-8 space-y-3">
              {area.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1 w-6 h-6 rounded-full bg-[var(--accent)]/10 grid place-content-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-[var(--accent)]" strokeWidth={2.4} />
                  </span>
                  <span className="text-[15.5px] text-[var(--text-main)]/85">{h}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Visit card */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 bg-[var(--primary)] text-[var(--bg)] rounded-3xl p-8 md:p-10">
              <div className="font-mono-eyebrow text-[var(--accent)]">
                {t("Visit us from")} {area.city}
              </div>
              <h3 className="font-serif-display mt-3 text-[32px] md:text-[40px] leading-tight">
                {area.distance}
              </h3>
              <div className="mt-6 flex items-start gap-3 text-white/85">
                <MapPin className="w-4 h-4 mt-1 text-[var(--accent)]" />
                <div className="text-sm leading-relaxed">
                  {PRACTICE.address}
                </div>
              </div>
              {area.landmarks?.length > 0 && (
                <ul className="mt-6 space-y-2 text-sm text-white/80">
                  {area.landmarks.map((l, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-[var(--accent)] mt-0.5 flex-shrink-0" />
                      {l}
                    </li>
                  ))}
                </ul>
              )}
              <a
                data-testid="local-directions-link"
                href={PRACTICE.mapUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]"
              >
                {t("Get directions")} <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* Featured services */}
      <section className="px-6 md:px-10 lg:px-16 py-16 md:py-24">
        <div className="text-center max-w-3xl mx-auto">
          <div className="font-mono-eyebrow text-[var(--text-muted)]">
            {t("Popular in")} {area.city}
          </div>
          <h2 className="font-serif-display text-[var(--primary)] mt-4 text-[36px] md:text-[52px] leading-tight tracking-tight">
            {t("Treatments")}{" "}
            <span className="italic text-[var(--accent)]">
              {area.city}
            </span>{" "}
            {t("patients ask about most.")}
          </h2>
          <SunDivider className="mt-5" />
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {featured.map((s) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              data-testid={`local-featured-${s.slug}`}
              className="group bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-6 hover:bg-[var(--primary)] hover:text-[var(--bg)] hover:border-[var(--primary)] transition-colors"
            >
              <div className="font-mono-eyebrow text-[var(--accent)]">{s.tag}</div>
              <div className="font-serif-display text-[26px] mt-2 leading-tight">
                {s.title}
              </div>
              <p className="mt-3 text-sm opacity-80 line-clamp-3">{s.short}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-sm">
                {t("Learn more")} <ArrowUpRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA + contact form */}
      <section className="px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-[var(--surface)]/50 border-t border-[var(--border)]">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-5"
          >
            <div className="font-mono-eyebrow text-[var(--accent)]">
              {t("Ready when you are")}
            </div>
            <h2 className="font-serif-display text-[var(--primary)] mt-3 text-[36px] md:text-[52px] leading-tight tracking-tight">
              {t("Book your visit from")}{" "}
              <span className="italic text-[var(--accent)]">
                {area.city}
              </span>.
            </h2>
            <p className="mt-5 text-[var(--text-muted)] leading-relaxed">
              {t("Send us a note and we'll get you scheduled within one business day. Prefer to talk?")}{" "}
              <a className="underline underline-offset-4 hover:text-[var(--accent)]" href={PRACTICE.phoneHref}>
                {PRACTICE.phone}
              </a>.
            </p>
          </motion.div>
          <div className="lg:col-span-7">
            <ContactForm />
          </div>
        </div>
      </section>
    </article>
  );
}
